import { getSurveyResponses } from './survey.service';
import type {
  SurveyResponse,
  AnalyticsMetrics,
  FilterState,
  Rating,
} from '@/types';

// Calculate category averages
const calculateCategoryAverage = (
  responses: SurveyResponse[],
  fieldNames: (keyof SurveyResponse['ratings'])[]
): number => {
  if (responses.length === 0) return 0;

  const sum = responses.reduce((acc, response) => {
    const categorySum = fieldNames.reduce((catAcc, fieldName) => {
      return catAcc + (response.ratings[fieldName] || 0);
    }, 0);
    return acc + categorySum / fieldNames.length;
  }, 0);

  return Number((sum / responses.length).toFixed(2));
};

// Calculate CSAT percentage (% with overall_overallSatisfied >= 4)
const calculateCSATPercentage = (responses: SurveyResponse[]): number => {
  if (responses.length === 0) return 0;

  const satisfiedCount = responses.filter(
    (r) => r.ratings.overall_overallSatisfied >= 4
  ).length;

  return Number(((satisfiedCount / responses.length) * 100).toFixed(2));
};

// Calculate recommendation percentage (% with overall_wouldRecommend >= 4)
const calculateRecommendationPercentage = (
  responses: SurveyResponse[]
): number => {
  if (responses.length === 0) return 0;

  const recommendCount = responses.filter(
    (r) => r.ratings.overall_wouldRecommend >= 4
  ).length;

  return Number(((recommendCount / responses.length) * 100).toFixed(2));
};

// Get analytics metrics for filtered responses
export const getAnalyticsMetrics = async (
  filters?: FilterState
): Promise<AnalyticsMetrics> => {
  const responses = await getSurveyResponses(filters);

  const productQualityFields: (keyof SurveyResponse['ratings'])[] = [
    'productQuality_expectationsMet',
    'productQuality_performanceAsExpected',
  ];

  const deliveryFields: (keyof SurveyResponse['ratings'])[] = [
    'delivery_timeliness',
    'delivery_processEfficient',
  ];

  const communicationFields: (keyof SurveyResponse['ratings'])[] = [
    'communication_clearTimely',
    'communication_promptResponses',
  ];

  const staffFields: (keyof SurveyResponse['ratings'])[] = [
    'staff_professionalCourteousHelpful',
    'staff_supportAdequateEffective',
  ];

  const priceFields: (keyof SurveyResponse['ratings'])[] = [
    'price_valueForMoney',
  ];

  const overallFields: (keyof SurveyResponse['ratings'])[] = [
    'overall_overallSatisfied',
    'overall_wouldRecommend',
  ];

  return {
    categoryAverages: {
      productQuality: calculateCategoryAverage(responses, productQualityFields),
      delivery: calculateCategoryAverage(responses, deliveryFields),
      communication: calculateCategoryAverage(responses, communicationFields),
      staff: calculateCategoryAverage(responses, staffFields),
      price: calculateCategoryAverage(responses, priceFields),
      overall: calculateCategoryAverage(responses, overallFields),
    },
    csatPercentage: calculateCSATPercentage(responses),
    recommendationPercentage: calculateRecommendationPercentage(responses),
    totalResponses: responses.length,
    dateRange: filters?.dateRange || {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      end: new Date().toISOString(),
    },
  };
};

// Get trend data for time series charts
export const getTrendData = async (
  filters?: FilterState,
  groupBy: 'day' | 'week' | 'month' = 'day'
): Promise<Array<{ date: string; value: number; label: string }>> => {
  const responses = await getSurveyResponses(filters);

  // Group responses by date
  const grouped = new Map<string, SurveyResponse[]>();

  responses.forEach((response) => {
    const date = new Date(response.createdAt);
    let key: string;

    if (groupBy === 'day') {
      key = date.toISOString().split('T')[0]!;
    } else if (groupBy === 'week') {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      key = weekStart.toISOString().split('T')[0]!;
    } else {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(response);
  });

  // Calculate average for each group
  return Array.from(grouped.entries())
    .map(([date, groupResponses]) => {
      const avg =
        groupResponses.reduce(
          (sum, r) => sum + r.ratings.overall_overallSatisfied,
          0
        ) / groupResponses.length;

      return {
        date,
        value: Number(avg.toFixed(2)),
        label: date,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

// Get distribution data for ratings
export const getRatingDistribution = async (
  filters?: FilterState,
  category: keyof SurveyResponse['ratings'] = 'overall_overallSatisfied'
): Promise<Array<{ rating: Rating; count: number; percentage: number }>> => {
  const responses = await getSurveyResponses(filters);
  const total = responses.length;

  if (total === 0) {
    return [1, 2, 3, 4, 5].map((r) => ({
      rating: r as Rating,
      count: 0,
      percentage: 0,
    }));
  }

  const distribution = new Map<Rating, number>();

  responses.forEach((response) => {
    const rating = response.ratings[category];
    distribution.set(rating, (distribution.get(rating) || 0) + 1);
  });

  return [1, 2, 3, 4, 5].map((r) => {
    const rating = r as Rating;
    const count = distribution.get(rating) || 0;
    return {
      rating,
      count,
      percentage: Number(((count / total) * 100).toFixed(2)),
    };
  });
};

